/**
 * Hook to get approval summary and request approval for a product
 */
import { useContext } from "react";
import useSWR from "swr";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { endpoints } from "../../../../../endpoints";
import type {
  ProductApprovals,
  RequestApprovalPayload,
} from "../../../../../types/types.Workflow";
import { useStoreState } from "../../../../../util/util";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";

// TODO: Backend needs to add response schema for POST request approval
interface RequestApprovalResponse {
  // Placeholder - backend needs to define this
}

export function useProductApprovals(productId: string | null) {
  const { storefront_id } = useStoreState();
  const { notifyError, notifySuccess } = useContext(Notifications);
  const { t } = useTranslation();

  // Fetch approval summary (stages with approvers)
  const { data, error, mutate, isValidating } = useSWR<ProductApprovals>(
    storefront_id && productId
      ? endpoints.v2_storefronts_id_integrations_products_id_approvals(
          storefront_id,
          productId
        )
      : null
  );

  // Request approval for the product
  const requestApproval = async (
    payload?: RequestApprovalPayload
  ): Promise<RequestApprovalResponse | null> => {
    if (!storefront_id || !productId) return null;

    try {
      const response = await axios.post<RequestApprovalResponse>(
        endpoints.v2_storefronts_id_integrations_products_id_approvals(
          storefront_id,
          productId
        ),
        payload ?? {}
      );
      notifySuccess(t("Approval request submitted successfully"));
      await mutate();
      return response.data;
    } catch (err) {
      notifyError(t("Failed to submit approval request"), { error: err });
      throw err;
    }
  };

  return {
    summary: data,
    stages: data?.stages ?? [],
    eligibleSkus: data?.eligible_skus ?? [],
    error,
    isLoading: !data && !error && !!productId,
    isValidating,
    mutate,
    requestApproval,
  };
}
